import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { buildRequestsFilter } from '@/modules/service-desk/shared/application-api';
import { FULFILMENT_STATUS_OPTIONS } from '@/modules/service-desk/shared/application-utils';
import { asExcelText, buildCsv, downloadCsv, exportDateStamp, formatRequestedOn } from '@/lib/csv';
import { useRequestFilters } from '@/modules/service-desk/shared/use-request-filters';
import { fetchAllPages } from '@/lib/fetch-all-pages';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-desk/shared/components/applications-table';
import { ExportCsvButton } from '@/modules/service-desk/shared/components/export-csv-button';
import {
  fetchIdCardFileUrls,
  useChangeIdCardStatus,
  useIdCardApplications,
  useIdCardFileUrls,
} from '../../hooks/use-id-card';
import { ID_CARD_APPLICATION_TYPES } from '../../utils/id-card-rules';
import { idCardApi } from '../../services/id-card.service';
import { createIdCardRequestColumns } from '../id-card-columns/id-card-columns';

const CSV_HEADERS = [
  'RequestedOn',
  'EmployeeId',
  'Name',
  'Email',
  'Designation',
  'MobileNumber',
  'BloodGroup',
  'ApplicationType',
  'PaymentMethod',
  'PaymentReference',
  'PhotoUrl',
  'PaymentReferenceUrl',
  'Status',
];

export const IdCardRequests = () => {
  const { t } = useTranslation();
  const canUpdateStatus = useHasPermission(HRM_PERMISSIONS.ID_CARD_APPLICATION.UPDATE_STATUS);
  const { filters, departments, departmentCondition, isScopeLoading } = useRequestFilters();
  const table = useApplicationsTable();
  const { resetPage } = table;

  useEffect(() => resetPage(), [filters, resetPage]);

  const filter = mergeWhere(
    buildRequestsFilter(filters, 'Name', departmentCondition),
    table.columnWhere
  );
  const { data, isLoading, error } = useIdCardApplications({
    pageNo: table.pagination.pageIndex + 1,
    pageSize: table.pagination.pageSize,
    filter,
    order: table.order,
  });
  const { data: fileUrls = {} } = useIdCardFileUrls(data?.items ?? []);
  const {
    mutateAsync: changeStatus,
    isPending: isSavingStatus,
    variables: savingStatus,
  } = useChangeIdCardStatus();
  const savingId = isSavingStatus ? savingStatus?.itemId : undefined;

  const columns = useMemo(
    () =>
      createIdCardRequestColumns({
        t,
        departments,
        canUpdateStatus,
        fileUrls,
        savingId,
        onChangeStatus: (application, status) =>
          changeStatus({ itemId: application.ItemId, status }),
      }),
    [t, departments, canUpdateStatus, fileUrls, savingId, changeStatus]
  );

  /** Legacy CSV: every row matching the current filters, with photo and receipt URLs resolved. */
  const exportCsv = async () => {
    const all = await fetchAllPages((pageNo, pageSize) =>
      idCardApi.list({ pageNo, pageSize, filter, order: table.order })
    );
    const urls = await fetchIdCardFileUrls(all);
    const rows = all.map((item) => ({
      RequestedOn: formatRequestedOn(item.AppliedDateUtc, item.Timezone),
      EmployeeId: item.EmployeeId,
      Name: item.Name,
      Email: item.Email,
      Designation: item.Designation,
      MobileNumber: asExcelText(item.MobileNumber),
      BloodGroup: item.BloodGroup,
      ApplicationType: t(
        ID_CARD_APPLICATION_TYPES.find((type) => type.value === item.ApplicationType)?.labelKey ??
          ''
      ),
      PaymentMethod: item.PaymentMethod,
      PaymentReference: item.PaymentReference,
      PhotoUrl: item.PhotoId ? urls[item.PhotoId] : '',
      PaymentReferenceUrl: item.PaymentReferenceId ? urls[item.PaymentReferenceId] : '',
      Status:
        t(FULFILMENT_STATUS_OPTIONS.find((o) => o.value === item.Status)?.labelKey ?? '') ||
        item.Status,
    }));
    const date = exportDateStamp();
    downloadCsv(`ID Card Requests_${date}`, buildCsv(CSV_HEADERS, rows));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Name, department and status are filtered from their columns. On a phone the download
          sits on the cards' line, beside the filter button. */}
      <div className="flex justify-end max-md:hidden">
        <ExportCsvButton onExport={exportCsv} className="w-auto" />
      </div>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading || isScopeLoading}
        error={error}
        table={table}
        mobileActions={<ExportCsvButton onExport={exportCsv} />}
      />
    </div>
  );
};
